"use client";

import { useEffect, useRef, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";

// Minimal typing for the Square Web Payments SDK loaded via <script>
// (we intentionally use `any` here to avoid coupling to @square/web-payments-sdk-types
// at runtime — the SDK is loaded from Square's CDN, not bundled)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-namespace */
declare global {
  interface Window {
    Square?: any;
    ApplePaySession?: any;
  }
  // Apple's <apple-pay-button> custom element (loaded from Apple's CDN).
  // Declaring it here so React/TS allow us to render it in JSX.
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          buttonstyle?: string;
          type?: string;
          locale?: string;
        },
        HTMLElement
      >;
    }
  }
}

const APPLE_PAY_SDK_URL = "https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js";

interface SquareCardFormProps {
  amount: number;
  buttonLabel?: string;
  processingLabel?: string;
  isProcessing: boolean;
  setIsProcessing: (b: boolean) => void;
  onPaymentSuccess: (sourceId: string) => Promise<void>;
  onPaymentError: (error: string) => void;
  /** Optional country code for Apple/Google Pay payment requests (defaults to "US"). */
  countryCode?: string;
  /** Optional reference label shown in the wallet sheet (defaults to "Rivky Wigs"). */
  walletLabel?: string;
}

const SANDBOX_SDK_URL = "https://sandbox.web.squarecdn.com/v1/square.js";
const PROD_SDK_URL = "https://web.squarecdn.com/v1/square.js";

export function SquareCardForm({
  amount,
  buttonLabel,
  processingLabel,
  isProcessing,
  setIsProcessing,
  onPaymentSuccess,
  onPaymentError,
  countryCode = "US",
  walletLabel = "Rivky Wigs",
}: SquareCardFormProps) {
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const googlePayBtnRef = useRef<HTMLDivElement>(null);
  const cardInstanceRef = useRef<any>(null);
  const applePayInstanceRef = useRef<any>(null);
  const googlePayInstanceRef = useRef<any>(null);
  const paymentsRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);

  // Square wallet APIs require an immutable PaymentRequest at init time.
  // We re-init the wallets whenever `amount` changes (only when the change
  // is meaningful — at least one cent).
  const amountKey = Math.round(amount * 100);

  useEffect(() => {
    let cancelled = false;

    // NEXT_PUBLIC_* vars MUST use dot notation for Next.js to inline them at build time.
    // Bracket notation is preserved as runtime lookup which fails in the browser (no process.env).
    const env = process.env.NEXT_PUBLIC_SQUARE_ENVIRONMENT || "sandbox";
    const appId = process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID;
    const locationId = process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID;
    const scriptSrc = env === "production" ? PROD_SDK_URL : SANDBOX_SDK_URL;

    if (!appId || !locationId) {
      setLoadError("Square is not configured. Please contact support.");
      return;
    }

    // Load Apple's SDK once so the <apple-pay-button> custom element
    // is registered. Only Safari ever uses it — skip on browsers
    // without ApplePaySession to save a request.
    if (
      typeof window !== "undefined" &&
      (window as any).ApplePaySession &&
      !document.querySelector(`script[src="${APPLE_PAY_SDK_URL}"]`)
    ) {
      const applePayScript = document.createElement("script");
      applePayScript.src = APPLE_PAY_SDK_URL;
      applePayScript.async = true;
      applePayScript.crossOrigin = "anonymous";
      document.head.appendChild(applePayScript);
    }

    async function initializeCard() {
      if (cancelled) return;
      if (!window.Square || !cardContainerRef.current) return;
      try {
        const payments = window.Square.payments(appId, locationId);
        paymentsRef.current = payments;
        const card = await payments.card();
        if (cancelled) return;
        await card.attach(cardContainerRef.current);
        if (cancelled) {
          try {
            await card.destroy();
          } catch {
            /* noop */
          }
          return;
        }
        cardInstanceRef.current = card;
        setLoaded(true);
        // Wallets are best-effort — failures here should not block the card form.
        initializeWallets().catch(() => {});
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "Failed to load payment form"
          );
        }
      }
    }

    async function initializeWallets() {
      const payments = paymentsRef.current;
      if (!payments || cancelled) return;

      // Build a fresh PaymentRequest. Square requires this for both Apple and
      // Google Pay so the wallet sheet can show the correct line items.
      const buildPaymentRequest = () =>
        payments.paymentRequest({
          countryCode,
          currencyCode: "USD",
          total: {
            amount: amount.toFixed(2),
            label: walletLabel,
          },
        });

      // ─── Apple Pay ───
      try {
        const applePayPaymentRequest = buildPaymentRequest();
        const applePay = await payments.applePay(applePayPaymentRequest);
        if (cancelled) return;
        applePayInstanceRef.current = applePay;
        setApplePayReady(true);
      } catch (err) {
        // applePay() throws when the device / browser doesn't support it,
        // the domain isn't verified, or the card isn't on file.
        const msg = err instanceof Error ? err.message : String(err);
        // The "not on Safari / unsupported method" case is EXPECTED on
        // every non-Safari visitor (Chrome, Android, Firefox) — just hide
        // the button, don't log it as an error. Only forward genuinely
        // unexpected failures (e.g. domain not verified) to /api/errors.
        const isUnsupported = /only available on safari|method unsupported|not supported|unsupported/i.test(msg);
        if (!isUnsupported) {
          console.warn("[SquareCardForm] Apple Pay init failed:", err);
          fetch("/api/errors", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: `Apple Pay init failed: ${msg}`,
              stack: err instanceof Error ? err.stack : undefined,
              url: typeof window !== "undefined" ? window.location.href : "",
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
              context: { kind: "apple-pay-init" },
            }),
            keepalive: true,
          }).catch(() => {});
        }
        applePayInstanceRef.current = null;
        setApplePayReady(false);
      }

      // ─── Google Pay ───
      // Square's Google Pay flow opens a popup for auth. iOS Safari (and
      // some embedded webviews) block third-party popups by default and
      // the resulting error (OR_BIBED_15) surfaces as an in-page "Google
      // Pay couldn't load properly" banner. Apple Pay is the right wallet
      // on iOS; skip Google Pay entirely there.
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && "ontouchend" in document);
      if (isIOS) {
        googlePayInstanceRef.current = null;
        setGooglePayReady(false);
      } else {
        try {
          const googlePayPaymentRequest = buildPaymentRequest();
          const googlePay = await payments.googlePay(googlePayPaymentRequest);
          if (cancelled) return;
          if (googlePayBtnRef.current) {
            await googlePay.attach(googlePayBtnRef.current, {
              buttonColor: "black",
              buttonType: "long",
              buttonSizeMode: "fill",
            });
          }
          if (cancelled) {
            try { await googlePay.destroy(); } catch { /* noop */ }
            return;
          }
          googlePayInstanceRef.current = googlePay;
          setGooglePayReady(true);
        } catch (err) {
          console.warn("[SquareCardForm] Google Pay init failed:", err);
          googlePayInstanceRef.current = null;
          setGooglePayReady(false);
        }
      }
    }

    if (window.Square) {
      // Square SDK is already loaded from a previous mount — init immediately
      // to avoid a race where we'd otherwise wait for a `load` event that will never fire.
      initializeCard();
    } else {
      // If another instance already started loading the script, wait for it —
      // but double-check `window.Square` in case the script finished loading
      // between our previous check and now.
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${scriptSrc}"]`
      );
      if (existing) {
        if (window.Square) {
          initializeCard();
        } else {
          existing.addEventListener("load", initializeCard, { once: true });
          existing.addEventListener(
            "error",
            () => {
              if (!cancelled) setLoadError("Failed to load Square SDK");
            },
            { once: true }
          );
        }
      } else {
        const script = document.createElement("script");
        script.src = scriptSrc;
        script.async = true;
        script.onload = initializeCard;
        script.onerror = () => {
          if (!cancelled) setLoadError("Failed to load Square SDK");
        };
        document.body.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      const card = cardInstanceRef.current;
      if (card) {
        card.destroy?.().catch(() => {});
        cardInstanceRef.current = null;
      }
      const ap = applePayInstanceRef.current;
      if (ap) {
        ap.destroy?.().catch(() => {});
        applePayInstanceRef.current = null;
      }
      const gp = googlePayInstanceRef.current;
      if (gp) {
        gp.destroy?.().catch(() => {});
        googlePayInstanceRef.current = null;
      }
    };
    // amountKey is intentionally a dep so the wallets re-init with the new total
    // when the cart changes (e.g. coupon applied mid-checkout).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountKey]);

  const tokenizeAndSubmit = async (instance: any, label: string) => {
    if (!instance || isProcessing) return;
    setIsProcessing(true);
    try {
      const result = await instance.tokenize();
      if (result.status === "OK" && result.token) {
        await onPaymentSuccess(result.token);
      } else {
        const firstError = result.errors?.[0];
        const message =
          firstError?.message ||
          firstError?.detail ||
          `${label} could not be processed. Please try again.`;
        onPaymentError(message);
        setIsProcessing(false);
      }
    } catch (err) {
      onPaymentError(
        err instanceof Error ? err.message : "Payment processing error"
      );
      setIsProcessing(false);
    }
  };

  const handleSubmit = () =>
    tokenizeAndSubmit(cardInstanceRef.current, "Card");

  const handleApplePay = () =>
    tokenizeAndSubmit(applePayInstanceRef.current, "Apple Pay");

  // Google Pay's Square button calls tokenize() automatically when clicked,
  // but we still attach an onclick on the wrapper as a fallback in case the
  // attached button is missed (e.g. CSP weirdness).
  const handleGooglePayWrapperClick = async () => {
    const gp = googlePayInstanceRef.current;
    if (!gp || isProcessing) return;
    // If Square's own button handled the click, tokenize() will already be
    // running — let it. Otherwise we trigger it ourselves.
    try {
      await tokenizeAndSubmit(gp, "Google Pay");
    } catch {
      /* swallowed inside tokenizeAndSubmit */
    }
  };

  const isSandbox =
    (process.env.NEXT_PUBLIC_SQUARE_ENVIRONMENT || "sandbox") !== "production";

  const showWallets = applePayReady || googlePayReady;

  return (
    <div className="space-y-4">
      {showWallets && (
        <div className="space-y-3">
          {applePayReady && (
            // Apple's official web component, loaded via their SDK in
            // the effect below. Renders the real Apple Pay logo button
            // (-webkit-appearance and U+F8FF glyph both proved flaky).
            <apple-pay-button
              buttonstyle="black"
              type="pay"
              locale="en-US"
              onClick={handleApplePay}
              style={{
                // Apple's custom element takes sizing via CSS variables.
                // These are the documented knobs; height must stay >= 30px.
                ["--apple-pay-button-width" as never]: "100%",
                ["--apple-pay-button-height" as never]: "48px",
                ["--apple-pay-button-border-radius" as never]: "8px",
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
                pointerEvents: isProcessing ? "none" : "auto",
              }}
            />
          )}
          {googlePayReady && (
            <div
              ref={googlePayBtnRef}
              onClick={handleGooglePayWrapperClick}
              className="min-h-[48px] w-full"
            />
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
              <span className="bg-white px-3 text-brand-muted">
                or pay with card
              </span>
            </div>
          </div>
        </div>
      )}

      <div
        className={
          "border rounded-lg p-4 bg-white transition-colors " +
          (loadError ? "border-red-300" : "border-gray-200")
        }
      >
        <div ref={cardContainerRef} className="min-h-[72px]">
          {!loaded && !loadError && (
            <div className="flex items-center gap-2 text-sm text-brand-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading secure payment form...</span>
            </div>
          )}
        </div>
        {loadError && (
          <p className="mt-2 text-xs text-red-600">{loadError}</p>
        )}
      </div>

      <Button
        variant="gold"
        size="lg"
        className="w-full"
        loading={isProcessing}
        onClick={handleSubmit}
        disabled={!loaded || isProcessing || !!loadError}
      >
        <Lock className="h-4 w-4 me-2" />
        {isProcessing
          ? processingLabel || "Processing..."
          : buttonLabel || `Pay $${amount.toFixed(2)}`}
      </Button>

      <p className="text-[11px] text-brand-muted text-center">
        Secured by Square. Your card details never touch our servers.
        {isSandbox && (
          <>
            <br />
            <span className="text-amber-600">
              Sandbox test card: 4111 1111 1111 1111, any future date, any CVV.
            </span>
          </>
        )}
      </p>
    </div>
  );
}
